import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ConfirmationModal from '@components/ConfirmationModal/ConfirmationModal';
import Loadable from '@components/Loadable/Loadable';
import {
  handleDeleteCannedResponse,
  handleGetCannedResponsesByCategory,
} from '@store/features/adminInquiries/cannedResponses/actions';
import {
  cannedResponsesErrorSelector,
  cannedResponsesIsDeletingSelector,
  cannedResponsesIsLoadingSelector,
  cannedResponsesSelector,
} from '@store/features/adminInquiries/cannedResponses/selectors';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';
import handleToast from '@utils/handleToast';

import CannedResponseItem from '../CannedResponseItem/CannedResponseItem';

import { ICannedResponsesListProps } from './CannedResponsesList.interface';

import './style.scss';

const CannedResponsesList: FC<ICannedResponsesListProps> = ({ categoryId, onApply }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const eventBus = useEventBus('cannedResponsesList');

  const items = useSelector(cannedResponsesSelector);
  const isLoading = useSelector(cannedResponsesIsLoadingSelector);
  const error = useSelector(cannedResponsesErrorSelector);
  const isDeleting = useSelector(cannedResponsesIsDeletingSelector);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleFetchList = useCallback(() => {
    categoryId && dispatch(handleGetCannedResponsesByCategory(categoryId));
  }, [dispatch, categoryId]);

  const handleCloseDeleteConfirmationDialog = useCallback(() => {
    setDeletingId(null);
  }, []);

  const handleDelete = useCallback(() => {
    deletingId && dispatch(handleDeleteCannedResponse(deletingId));
  }, [dispatch, deletingId]);

  useEffect(() => {
    eventBus.on(EventType.ADMIN_DELETE_CANNED_RESPONSE_SUCCESS, () => {
      handleToast('success', t('adminInquiries.cannedResponseDeleted'));
      handleCloseDeleteConfirmationDialog();
      handleFetchList();
    });

    return () => eventBus.off(EventType.ADMIN_DELETE_CANNED_RESPONSE_SUCCESS);
  }, [t, eventBus, handleCloseDeleteConfirmationDialog, handleFetchList]);

  return (
    <>
      <div className="canned-responses-list">
        <div className="canned-responses-list__title">
          {t('adminInquiries.addedCannedResponses')}
        </div>

        <Loadable
          isLoading={isLoading}
          error={error && t('errors.userEnteredSupplementError')}
          onRetry={handleFetchList}
          loaderClassName="canned-responses-list__loader"
          loaderSize={36}
        >
          {items?.length ? (
            items.map(item => (
              <CannedResponseItem
                data={item}
                onApply={onApply}
                onDelete={setDeletingId}
                key={item.id}
              />
            ))
          ) : (
            <div className="canned-responses-list__placeholder">
              {t('adminInquiries.noCannedResponses')}
            </div>
          )}
        </Loadable>
      </div>

      <ConfirmationModal
        open={deletingId !== null}
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={handleCloseDeleteConfirmationDialog}
        title={t('adminInquiries.deleteCannedResponseConfirmation')}
        rightActionTitle={t('adminInquiries.deleteCannedResponse')}
      />
    </>
  );
};

export default CannedResponsesList;
